import React from "react";
import DatasourceCardLayout from "../../../shared/cards/DatasourceCardLayout";
import AccordionLayout, { accordionExpandedCacheKey } from "../../../shared/accordions/AccordionLayout";
import { workbookCache } from "../../../../excel/workbook/cache";
import { useProject } from "../../../context/ProjectContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useBanner } from "../../../context/BannerContext";
import { EDIPHI_DATASOURCE_META } from "../meta";
import {
  LAST_REFRESHED,
  LAST_REFRESHED_FORMAT,
  EDIPHI_DATASOURCE_DELETE_NOTICE,
  GCGR_REMOVED_DATA_REMAINS,
} from "../../../constants";
import { formatCurrency, formatCompactCurrency } from "../format";
import { openGcgrInEdiphi } from "../../../../utils/links";
import { clearReportWorksheets } from "../../../../excel/reports/api/registry";
import { reportTypeOfTemplate } from "../../../hooks/useReportCards";
import { useGcgrLoader } from "../../../hooks/useGcgrLoader";
import { useCardSummary } from "../../../hooks/useCardSummary";

interface GcgrDatasourceCardProps {
  onCardClick: () => void;
}

const DETAILS_CACHE_KEY = accordionExpandedCacheKey("gcgr");

const GcgrDatasourceCard = ({ onCardClick }: GcgrDatasourceCardProps) => {
  const { selectedGcgr, setSelectedGcgr } = useProject();
  const { removeDatasource } = useDatasources();
  const { showBanner } = useBanner();
  const { loadGcgr, clearGcgr } = useGcgrLoader();
  const summary = useCardSummary("gcgr");

  if (!selectedGcgr) return null;

  const totalGeneralConditions = summary
    ? summary.generalConditions + summary.preconStaffing + summary.constructionStaffing
    : undefined;
  const metricValue = (value: number | null | undefined) => (value == null ? "—" : formatCurrency(value));

  const handleRemoveCard = async () => {
    removeDatasource("ediphi", "gcgr");
    setSelectedGcgr(null);
    void workbookCache.delete(DETAILS_CACHE_KEY);

    // The React state above is already cleared, so a failure here leaves the card gone but the
    // workbook still naming the GCGR — say so rather than failing silently.
    try {
      await clearReportWorksheets((template) => reportTypeOfTemplate(template) === "gcgr");
      await clearGcgr();
    } catch {
      showBanner(GCGR_REMOVED_DATA_REMAINS, "error");
    }
  };

  return (
    <DatasourceCardLayout
      header={{
        icon: EDIPHI_DATASOURCE_META.gcgr.icon,
        text: EDIPHI_DATASOURCE_META.gcgr.label,
      }}
      card={{
        text: summary?.name ?? selectedGcgr.name,
        subtext: summary ? formatCompactCurrency(summary.contractValue) : "",
      }}
      onCardClick={onCardClick}
      onLaunch={() => openGcgrInEdiphi(selectedGcgr.project, selectedGcgr.id)}
      onRefresh={() => void loadGcgr(selectedGcgr)}
      onClose={handleRemoveCard}
      deleteNotice={EDIPHI_DATASOURCE_DELETE_NOTICE}
    >
      <AccordionLayout
        details={[
          { label: "Contract Value", value: metricValue(summary?.contractValue) },
          { label: "General Requirements", value: metricValue(summary?.generalRequirements) },
          { label: "General Conditions", value: metricValue(summary?.generalConditions) },
          { label: "Precon Staffing", value: metricValue(summary?.preconStaffing) },
          { label: "Construction Staffing", value: metricValue(summary?.constructionStaffing) },
          { label: "Total General Conditions", value: metricValue(totalGeneralConditions) },
        ]}
        lastUpdated={summary?.lastRefreshedAt}
        lastUpdatedLabel={LAST_REFRESHED}
        lastUpdatedFormat={LAST_REFRESHED_FORMAT}
        cacheKey={DETAILS_CACHE_KEY}
      />
    </DatasourceCardLayout>
  );
};

export default GcgrDatasourceCard;
